import type {EagColumnState} from '../../types/EagColumnState';
import {arraysShallowEqual} from '../../utils/arraysShallowEqual';
import type {EagGridState} from '../createEagGridStore';

/**
 * Wraps a pure `columnStates → columnStates` transform in the caching strategy
 * every column selector shares:
 *
 * - **WeakMap keyed on `state.columnStates`** — the same input array reference
 *   always yields the same output reference. useSyncExternalStore calls
 *   getSnapshot 2-3× per render and checks consistency with Object.is; ramda
 *   `filter`/`sortBy` otherwise return a fresh array each call and React
 *   re-renders infinitely.
 * - **`lastResult` structural-equality short-circuit** — when the input array is
 *   a new reference but the transform produces a structurally-equal result (e.g.
 *   only an unrelated column's width changed), the PREVIOUS reference is returned
 *   so downstream consumers don't re-render.
 *
 * Each call allocates its own private `cache` + `lastResult`. Call it ONCE at
 * module level for a shared singleton (e.g. {@link useOrderedVisibleColumns}), or
 * ONCE PER factory invocation for an isolated cache (e.g.
 * {@link createCachedColumnSelector}). Selectors that share an input array but
 * compute different results MUST NOT share a cache — they would collide on the
 * WeakMap key.
 */
export function withColumnStatesCache(
  compute: (columnStates: EagColumnState[]) => EagColumnState[]
): (state: EagGridState) => EagColumnState[] {
  const cache = new WeakMap<EagColumnState[], EagColumnState[]>();
  let lastResult: EagColumnState[] | null = null;

  return (state: EagGridState): EagColumnState[] => {
    const cached = cache.get(state.columnStates);
    if (cached !== undefined) {
      return cached;
    }

    const result = compute(state.columnStates);

    // Structural equality: if the result hasn't changed (same elements in same
    // order), return the previous reference to prevent downstream re-renders.
    if (lastResult !== null && arraysShallowEqual(lastResult, result)) {
      cache.set(state.columnStates, lastResult);
      return lastResult;
    }

    lastResult = result;
    cache.set(state.columnStates, result);
    return result;
  };
}
